import styles from 'css/src/components/pricing/pricing.module.scss';

import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { format, parseISO } from 'date-fns';
import type { Plan } from 'js/src/client';
import { ApiService, Urls } from 'js/src/client';
import JumboCta from 'js/src/components/common/JumboCta';
import LogoImages from 'js/src/components/common/LogoImages';
import { Toast } from 'js/src/components/common/Toast';
import Callout, { CALLOUT_TYPES } from 'js/src/components/common/ui/Callout';
import BillingPeriodToggle from 'js/src/components/pricing/BillingPeriodToggle';
import ComparisonTable from 'js/src/components/pricing/ComparisonTable';
import NotificationBlock from 'js/src/components/pricing/NotificationBlock';
import PlanBlockList from 'js/src/components/pricing/PlanBlockList';
import PlanSwitchErrorHandlerModal from 'js/src/components/pricing/PlanSwitchErrorHandlerModal';
import SubscriptionChangeModal from 'js/src/components/pricing/SubscriptionChangeModal';
import type { PlanSwitchErrorInfo } from 'js/src/components/pricing/types';
import { ANALYTICS_QS, UPGRADE_STATUS } from 'js/src/components/pricing/types';
import CompanyInfoSection from 'js/src/components/public-pages/common-sections/CompanyInfoSection';
import { logoImages } from 'js/src/components/public-pages/homepage-data';
import {
    handlePlanSwitchError as handlePlanSwitchErrorThunk,
    reloadPageSuccess,
    retrySubscriptionChange as retrySubscriptionChangeThunk,
    switchToPlan as switchToPlanThunk,
    togglePlanSwitchErrorModal as togglePlanSwitchErrorModalThunk,
    updateSubscription as updateSubscriptionThunk,
    waitForPlanToUpdate,
} from 'js/src/data/pricing/pricing-operations';
import {
    setAnnualPlans,
    setChangingSubscription,
    setCouponStatus,
    setMonthlyPlans,
    setSelectedMonthly,
    setShowSubscriptionChangeConfirmation,
    setSubscriptionDetails,
    setUpgradeStatus,
    setUserHasPaymentMethodSetup,
} from 'js/src/data/pricing/pricing-slice';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import { gaSendPlanPurchase } from 'js/src/libs/ga-ecommerce';
import { getQueryParam } from 'js/src/libs/querystring';
import QueryStringManipulator from 'js/src/libs/querystring-manipulator';
import type { RootState } from 'js/src/libs/RootState';
import { getPlanDisplayName } from 'js/src/libs/server-utils';

const placeholderPlans = Array(4).fill(null);

const isFreePlan = (plan: Plan): boolean => {
    return plan.price === 0 || plan.name === '';
};

const isPaidPlan = (plan: Plan): boolean => {
    return !isFreePlan(plan);
};

const Pricing = React.memo(function Pricing() {
    const dispatch = useDispatch();
    const [openAccordionItem, setOpenAccordionItem] = React.useState<string | null>(null);

    // Redux state
    const selectedMonthly = useSelector((state: RootState) => state.pricing.selectedMonthly);
    const monthlyPlans = useSelector((state: RootState) => state.pricing.monthlyPlans);
    const annualPlans = useSelector((state: RootState) => state.pricing.annualPlans);
    const couponStatus = useSelector((state: RootState) => state.pricing.couponStatus);
    const showSubscriptionChangeConfirmation = useSelector(
        (state: RootState) => state.pricing.showSubscriptionChangeConfirmation,
    );
    const changingSubscription = useSelector(
        (state: RootState) => state.pricing.changingSubscription,
    );
    const upgradeStatus = useSelector((state: RootState) => state.pricing.upgradeStatus);
    const subscriptionDetails = useSelector(
        (state: RootState) => state.pricing.subscriptionDetails,
    );
    const confirmationModalData = useSelector(
        (state: RootState) => state.pricing.confirmationModalData,
    );
    const showPlanSwitchErrorModal = useSelector(
        (state: RootState) => state.pricing.showPlanSwitchErrorModal,
    );
    const planSwitchErrorInfo = useSelector(
        (state: RootState) => state.pricing.planSwitchErrorInfo,
    );

    const showAnnualPromo = Urls.SPECIAL_PRICING === window.location.pathname;

    const fbqIntervalRef = React.useRef<number | null>(null);
    const gaIntervalRef = React.useRef<number | null>(null);
    const gtagIntervalRef = React.useRef<number | null>(null);

    const sendFacebookPurchase = React.useCallback((value: number, plan_name: string) => {
        // Check to make sure fbq has loaded and initialized before sending the event
        if (
            typeof fbq === 'undefined' ||
            typeof fbq.getState !== 'function' ||
            fbq.getState().pixels.length < 1
        ) {
            return;
        }

        if (fbqIntervalRef.current !== null) {
            window.clearInterval(fbqIntervalRef.current);
            fbqIntervalRef.current = null;
        }

        fbq('track', 'Purchase', {
            value: value,
            currency: 'USD',
            content_name: plan_name,
        });
    }, []);

    const sendGoogleAnalyticsPurchase = React.useCallback((value: number, plan_name: string) => {
        if (typeof ga === 'undefined') {
            return;
        }

        if (gaIntervalRef.current !== null) {
            window.clearInterval(gaIntervalRef.current);
            gaIntervalRef.current = null;
        }

        gaSendPlanPurchase(plan_name, value);
    }, []);

    const sendGoogleAdsPurchase = React.useCallback((value: number, plan_name: string) => {
        if (typeof gtag === 'undefined') {
            return;
        }

        if (gtagIntervalRef.current !== null) {
            window.clearInterval(gtagIntervalRef.current);
            gtagIntervalRef.current = null;
        }

        gtag('event', 'conversion', {
            send_to: 'AW-835353444/eImVCPOo4IIBEOT2qY4D',
            value: value,
            currency: 'USD',
            plan_name: plan_name,
        });

        gtag('event', 'conversion', {
            send_to: 'AW-372587568/3JJyCMDehpICELD41LEB',
            value: value,
            currency: 'USD',
            plan_name: plan_name,
        });
    }, []);

    const recordAnalytics = React.useCallback(() => {
        const analyticsValue = getQueryParam(ANALYTICS_QS.KEY);
        if (analyticsValue !== undefined && analyticsValue !== ANALYTICS_QS.DONE) {
            // This handles our 'thank you' page after checkout, to record analytics.
            // The page is loaded with the new plan the user just upgraded to, while the old
            // plan info is passed in the query string.

            // send facebook/google conversion event if we've upgraded from the free plan
            const old_plan_price = parseInt(analyticsValue);
            if (old_plan_price === 0 && !!SERVER.USER) {
                // New plan data
                let dollars_per_month = SERVER.USER.plan_price;
                const plan_name = SERVER.USER.plan;
                const is_annual = SERVER.USER.plan_billing_cycle === 'annual';

                // calculate dollars per month
                if (is_annual) {
                    dollars_per_month /= 12.0;
                }

                // We use setInterval to send these because loading the third party scripts
                // happens asynchronously and may not be ready when this component is mounted
                // send facebook event
                fbqIntervalRef.current = window.setInterval(() => {
                    sendFacebookPurchase(dollars_per_month, plan_name);
                }, 200);

                // send google analytics
                gaIntervalRef.current = window.setInterval(() => {
                    sendGoogleAnalyticsPurchase(dollars_per_month, plan_name);
                }, 200);

                // send google ads
                gtagIntervalRef.current = window.setInterval(() => {
                    sendGoogleAdsPurchase(dollars_per_month, plan_name);
                }, 200);
            }

            // update the location so if the user refreshes the page, we won't report again
            const new_location = QueryStringManipulator.updateQueryStringParameter(
                window.location.toString(),
                ANALYTICS_QS.KEY,
                ANALYTICS_QS.DONE,
            );
            window.history.replaceState({ path: new_location }, '', new_location);
        }
    }, [sendFacebookPurchase, sendGoogleAnalyticsPurchase, sendGoogleAdsPurchase]);

    const loadPlans = React.useCallback(() => {
        ApiService.pricingPlansRetrieve({ query: { coupon: getQueryParam('cpn')! } })
            .then((response) => {
                dispatch(setMonthlyPlans(response.data.monthly_plans));
                dispatch(setAnnualPlans(response.data.annual_plans));
                dispatch(setCouponStatus(response.data.coupon_status));
            })
            .catch((error: unknown) => {
                ErrorReporter.capture(error);
                Toast.error('Error loading pricing plans. Please refresh page.');
            });
    }, [dispatch]);

    const loadPaymentMethodStatus = React.useCallback(() => {
        // don't load for logged out users
        if (SERVER.USER === null) {
            return;
        }

        ApiService.pricingPaymentMethodStatusRetrieve()
            .then((response) => {
                dispatch(setUserHasPaymentMethodSetup(response.data.has_payment_configured));
            })
            .catch((error: unknown) => {
                ErrorReporter.capture(error);
                Toast.error('Error loading payment method status. Please refresh page.');
            });
    }, [dispatch]);

    const loadSubscriptionDetails = React.useCallback(() => {
        if (!SERVER.USER) {
            // not logged in, don't try loading the subscription
            return;
        }
        ApiService.pricingSubscriptionRetrieve()
            .then((response) => {
                dispatch(setSubscriptionDetails(response.data));
            })
            .catch((error: unknown) => {
                ErrorReporter.capture(error);
                Toast.error('Error loading subscription details. Please refresh page.');
            });
    }, [dispatch]);

    React.useEffect(() => {
        recordAnalytics();
        loadPlans();
        loadPaymentMethodStatus();
        loadSubscriptionDetails();
    }, [loadPaymentMethodStatus, loadPlans, loadSubscriptionDetails, recordAnalytics]);

    React.useEffect(() => {
        let upgradeStatusInitial = null;
        let changingSubscriptionInitial = false;
        if (SERVER.USER) {
            if (getQueryParam('success') === 'true') {
                upgradeStatusInitial = UPGRADE_STATUS.SUCCESS;
            } else if (getQueryParam('pm_updated') === 'true') {
                const selected_plan = getQueryParam('selected_plan');
                if (SERVER.USER.plan === selected_plan) {
                    upgradeStatusInitial = UPGRADE_STATUS.SUCCESS;
                } else {
                    changingSubscriptionInitial = true;
                }
            }
        }

        dispatch(setChangingSubscription(changingSubscriptionInitial));
        dispatch(setUpgradeStatus(upgradeStatusInitial));

        if (getQueryParam('pm_updated') === 'true' && changingSubscriptionInitial) {
            const selected_plan = getQueryParam('selected_plan');
            dispatch(waitForPlanToUpdate(selected_plan!));
        }
    }, [dispatch]);

    React.useEffect(() => {
        // Use existing account billing cycle (if any) - free users default to annual.
        if (subscriptionDetails?.is_monthly !== undefined && subscriptionDetails.price > 0) {
            dispatch(setSelectedMonthly(subscriptionDetails.is_monthly));
        }
    }, [subscriptionDetails?.is_monthly, subscriptionDetails?.price, dispatch]);

    const scrollToPlans = React.useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        const elem = document.getElementById('feature-comparison');
        if (!elem) throw new Error('Could not find element to scroll to');
        elem.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        });
    }, []);

    const selectMonthly = React.useCallback(() => {
        dispatch(setSelectedMonthly(true));
    }, [dispatch]);

    const selectYearly = React.useCallback(() => {
        dispatch(setSelectedMonthly(false));
    }, [dispatch]);

    const renewSubscription = React.useCallback(() => {
        analytics.track('Subscription renewed');
        dispatch(setChangingSubscription(true));
        ApiService.pricingRenewSubscriptionCreate()
            .then((response) => {
                reloadPageSuccess();
            })
            .catch((error: unknown) => {
                ErrorReporter.capture(error);
                Toast.error(
                    'We could not automatically renew your subscription, please contact us at support@lumen5.com',
                );
                dispatch(setChangingSubscription(false));
            });
    }, [dispatch]);

    const switchToPlan = React.useCallback(
        (plan: Plan, with_trial: boolean) => {
            dispatch(switchToPlanThunk(plan, with_trial));
        },
        [dispatch],
    );

    const retrySubscriptionChange = React.useCallback(() => {
        dispatch(retrySubscriptionChangeThunk());
    }, [dispatch]);

    const updateSubscription = React.useCallback(
        (email: string, plan: Plan, with_trial: boolean) => {
            dispatch(updateSubscriptionThunk(email, plan, with_trial));
        },
        [dispatch],
    );

    const closeSubscriptionChangeModal = React.useCallback(() => {
        dispatch(setShowSubscriptionChangeConfirmation(false));
    }, [dispatch]);

    const handlePlanSwitchError = React.useCallback(
        (errorInfo: PlanSwitchErrorInfo) => {
            dispatch(handlePlanSwitchErrorThunk(errorInfo));
        },
        [dispatch],
    );

    const togglePlanSwitchErrorModal = React.useCallback(
        (visibilityFlag: boolean) => {
            dispatch(togglePlanSwitchErrorModalThunk(visibilityFlag));
        },
        [dispatch],
    );

    let loadingNode = null;
    let autoRenewalNode = null;
    let pausedSubscriptionNode = null;
    if (changingSubscription) {
        loadingNode = (
            <div className={styles['loadingOverlay']}>
                <p>Loading...</p>
            </div>
        );
    }
    const planDisplayName = getPlanDisplayName();
    if (subscriptionDetails?.is_paused) {
        const pause_resumes_at = subscriptionDetails.pause_resumes_at;
        let resume_text = '';
        if (pause_resumes_at) {
            const resumeDate =
                typeof pause_resumes_at === 'string'
                    ? parseISO(pause_resumes_at)
                    : new Date(pause_resumes_at);
            resume_text = ` It will resume on ${format(resumeDate, 'MMM d, yyyy')}.`;
        }
        pausedSubscriptionNode = (
            <div className="col-md-12 my-l5-8">
                <Callout
                    msgType={CALLOUT_TYPES.ERROR}
                    title={`Your ${planDisplayName} subscription is currently paused.`}
                    text={resume_text}
                    ctaText="Manage subscription"
                    onCtaClick={() => {
                        window.location.href = Urls.BILLING_SETTINGS;
                    }}
                />
            </div>
        );
    } else if (subscriptionDetails?.cancel_at_period_end) {
        const next_billing_date = subscriptionDetails.next_billing_date;
        if (next_billing_date === null) {
            throw new Error('next_billing_date is null');
        }
        const nextBillingDate =
            typeof next_billing_date === 'string'
                ? parseISO(next_billing_date)
                : new Date(next_billing_date);
        const next_billing_text = format(nextBillingDate, 'MMM d, yyyy');
        autoRenewalNode = (
            <div className="col-md-12 my-l5-8">
                <Callout
                    msgType={CALLOUT_TYPES.ERROR}
                    title={`Your ${planDisplayName} subscription is set to not renew and you will be downgraded on ${next_billing_text}.`}
                    ctaText="Turn on auto-renewal"
                    onCtaClick={renewSubscription}
                />
            </div>
        );
    }

    let billingPeriodToggleNode = null;
    const isCouponValid = couponStatus?.valid;
    if (!isCouponValid && !showAnnualPromo) {
        // don't show the period toggle if there's a coupon active so that users can't
        // pick annual plans and get the annual discount and the coupon!
        billingPeriodToggleNode = (
            <div className={`row ${styles['section-3']}`}>
                <div className="col d-flex justify-content-center align-items-center">
                    <BillingPeriodToggle
                        monthly={selectedMonthly}
                        selectMonthly={selectMonthly}
                        selectYearly={selectYearly}
                    />
                    <br />
                </div>
            </div>
        );
    }

    const showMonthlyPlans = (selectedMonthly || isCouponValid) && !showAnnualPromo;

    const communityPlans = React.useMemo(() => {
        const plans = showMonthlyPlans ? monthlyPlans : annualPlans;
        return plans.filter(isFreePlan);
    }, [showMonthlyPlans, monthlyPlans, annualPlans]);

    const paidPlans = React.useMemo(() => {
        const plans = showMonthlyPlans ? monthlyPlans : annualPlans;
        return plans.filter(isPaidPlan).filter((plan) => !plan.hide_in_public_pricing_page);
    }, [showMonthlyPlans, monthlyPlans, annualPlans]);

    return (
        <div className={`${styles['pricing-page-container']} overflow-x-hidden`}>
            <div className="container">
                {/* section 1 - pricing header */}
                <div
                    className={`row justify-content-center align-items-center ${styles['section-1']}`}
                >
                    <div className="col-md-4"></div>
                    <div className="col-md-4">
                        <div className="d-flex justify-content-center">
                            <h5>PRICING</h5>
                        </div>
                    </div>
                    <div className="col-md-4"></div>
                </div>
                {/* section 2 - pricing subheader */}
                <div
                    className={`row justify-content-center align-items-center ${styles['section-2']}`}
                >
                    <div className="col-md-2"></div>
                    <div className="col-md-8">
                        <div className="d-flex justify-content-center">
                            <h3>Pricing that scales with your video strategy</h3>
                        </div>
                    </div>
                    <div className="col-md-2"></div>
                </div>
            </div>
            <div className="container">
                {/* section 3 - billing period toggle */}
                {billingPeriodToggleNode}
                <div className="row justify-content-center align-items-center ">
                    <div className="col-8">
                        <div className="d-flex flex-column align-items-center">
                            {loadingNode}
                            {pausedSubscriptionNode}
                            {autoRenewalNode}
                            <NotificationBlock
                                couponStatus={couponStatus}
                                upgradeStatus={upgradeStatus ?? undefined}
                                planName={planDisplayName}
                                retryUpgradeWithNewCard={retrySubscriptionChange}
                            />
                        </div>
                    </div>
                </div>
            </div>
            {/* section 4 - plans */}
            <div className="container">
                <div
                    className={`d-flex flex-sm-row flex-wrap justify-content-center align-items-stretch ${styles['section-4']}`}
                >
                    <PlanBlockList
                        plans={paidPlans.length ? paidPlans : placeholderPlans}
                        switchToPlan={switchToPlan}
                        handlePlanSwitchError={handlePlanSwitchError}
                        scrollToPlans={scrollToPlans}
                    />
                    <PlanBlockList
                        plans={communityPlans}
                        switchToPlan={switchToPlan}
                        handlePlanSwitchError={handlePlanSwitchError}
                        scrollToPlans={scrollToPlans}
                    />
                </div>
            </div>

            {/* section 6 - social proof */}
            <div className="my-l5-48">
                <LogoImages logos={logoImages} />
            </div>

            {/* section 8 - comparison table */}
            <div id="feature-comparison" className="container py-l5-48 my-l5-48">
                <h3 className="text-center mb-l5-48">Compare features across plans</h3>
                <ComparisonTable
                    plans={paidPlans}
                    switchToPlan={switchToPlan}
                    isAnnualPromo={showAnnualPromo}
                    handlePlanSwitchError={handlePlanSwitchError}
                />
            </div>

            {/* section 9 - case study carousel */}
            <div className="container py-l5-48 my-l5-48">
                <CompanyInfoSection />
            </div>

            {/* section 10 - faqs */}
            <div className="container pb-l5-48">
                <div className="row mb-l5-16">
                    <div className="col-md-12 my-l5-48">
                        <h3 className="text-center my-l5-24">Frequently asked questions</h3>
                    </div>
                </div>
                <div className="row justify-content-center">
                    <div className="col-lg-8 col-md-10">
                        <div className={styles['faq-list']}>
                            <div className={styles['faq-item']}>
                                <button
                                    className={`${styles['faq-question']} btn btn-link`}
                                    onClick={() =>
                                        setOpenAccordionItem(
                                            openAccordionItem === 'item1' ? null : 'item1',
                                        )
                                    }
                                    type="button"
                                    aria-expanded={openAccordionItem === 'item1'}
                                >
                                    <span className={styles['faq-question-text']}>
                                        Which Lumen5 plan is best for my team?
                                    </span>
                                    <span
                                        className={`${styles['faq-icon']} ${openAccordionItem === 'item1' ? styles['expanded'] : ''}`}
                                    >
                                        +
                                    </span>
                                </button>
                                <div
                                    className={`collapse ${openAccordionItem === 'item1' ? 'show' : ''}`}
                                >
                                    <div className={styles['faq-answer']}>
                                        It depends on your goals. The Basic and Starter plans are
                                        ideal for individual creators who need simple, fast video
                                        creation. Pro is best for small businesses that require
                                        advanced branding, premium media, and more customization.
                                        The Team plan is built for organizations that need custom
                                        templates, collaboration features, workflow controls,
                                        translations, SSO, and enterprise grade support.
                                    </div>
                                </div>
                            </div>
                            <div className={styles['faq-item']}>
                                <button
                                    className={`${styles['faq-question']} btn btn-link`}
                                    onClick={() =>
                                        setOpenAccordionItem(
                                            openAccordionItem === 'item2' ? null : 'item2',
                                        )
                                    }
                                    type="button"
                                    aria-expanded={openAccordionItem === 'item2'}
                                >
                                    <span className={styles['faq-question-text']}>
                                        What makes the Team plan different from the Pro plan?
                                    </span>
                                    <span
                                        className={`${styles['faq-icon']} ${openAccordionItem === 'item2' ? styles['expanded'] : ''}`}
                                    >
                                        +
                                    </span>
                                </button>
                                <div
                                    className={`collapse ${openAccordionItem === 'item2' ? 'show' : ''}`}
                                >
                                    <div className={styles['faq-answer']}>
                                        The Team plan includes everything in Pro plus enterprise
                                        capabilities such as custom branded templates, bespoke scene
                                        designs, workspace and user management, translation tools,
                                        SSO, approval workflows, DAM integrations, and a dedicated
                                        customer success manager. It is designed for companies
                                        scaling video creation across multiple teams or locations.
                                    </div>
                                </div>
                            </div>
                            <div className={styles['faq-item']}>
                                <button
                                    className={`${styles['faq-question']} btn btn-link`}
                                    onClick={() =>
                                        setOpenAccordionItem(
                                            openAccordionItem === 'item3' ? null : 'item3',
                                        )
                                    }
                                    type="button"
                                    aria-expanded={openAccordionItem === 'item3'}
                                >
                                    <span className={styles['faq-question-text']}>
                                        Does Lumen5 support brand consistency across video content?
                                    </span>
                                    <span
                                        className={`${styles['faq-icon']} ${openAccordionItem === 'item3' ? styles['expanded'] : ''}`}
                                    >
                                        +
                                    </span>
                                </button>
                                <div
                                    className={`collapse ${openAccordionItem === 'item3' ? 'show' : ''}`}
                                >
                                    <div className={styles['faq-answer']}>
                                        Yes. Starter and Pro include brand kits, custom colors, and
                                        font uploads. The Team plan adds bespoke branded templates,
                                        custom transitions, and support from the Lumen5 Creative
                                        Services team so every exported video stays aligned with
                                        your brand identity across teams and regions.
                                    </div>
                                </div>
                            </div>
                            <div className={styles['faq-item']}>
                                <button
                                    className={`${styles['faq-question']} btn btn-link`}
                                    onClick={() =>
                                        setOpenAccordionItem(
                                            openAccordionItem === 'item4' ? null : 'item4',
                                        )
                                    }
                                    type="button"
                                    aria-expanded={openAccordionItem === 'item4'}
                                >
                                    <span className={styles['faq-question-text']}>
                                        What AI features are included in each plan?
                                    </span>
                                    <span
                                        className={`${styles['faq-icon']} ${openAccordionItem === 'item4' ? styles['expanded'] : ''}`}
                                    >
                                        +
                                    </span>
                                </button>
                                <div
                                    className={`collapse ${openAccordionItem === 'item4' ? 'show' : ''}`}
                                >
                                    <div className={styles['faq-answer']}>
                                        All paid plans include AI Script Composer, AI Voiceover, and
                                        high quality AI media. The Basic plan includes standard AI
                                        features, while Starter and Pro offer expanded AI
                                        capabilities and longer AI voiceover limits. The Team plan
                                        introduces advanced capabilities like translations and
                                        custom AI Voice options.
                                    </div>
                                </div>
                            </div>
                            <div className={styles['faq-item']}>
                                <button
                                    className={`${styles['faq-question']} btn btn-link`}
                                    onClick={() =>
                                        setOpenAccordionItem(
                                            openAccordionItem === 'item5' ? null : 'item5',
                                        )
                                    }
                                    type="button"
                                    aria-expanded={openAccordionItem === 'item5'}
                                >
                                    <span className={styles['faq-question-text']}>
                                        How does video translation work on the higher tier plans?
                                    </span>
                                    <span
                                        className={`${styles['faq-icon']} ${openAccordionItem === 'item5' ? styles['expanded'] : ''}`}
                                    >
                                        +
                                    </span>
                                </button>
                                <div
                                    className={`collapse ${openAccordionItem === 'item5' ? 'show' : ''}`}
                                >
                                    <div className={styles['faq-answer']}>
                                        Translation credits allow you to create localized versions
                                        of your videos. Pro users receive sixty translations per
                                        year, while Team customers receive unlimited translations.
                                        This is especially helpful for global marketing, internal
                                        communications, and learning and development teams that need
                                        multi language content.
                                    </div>
                                </div>
                            </div>
                            <div className={styles['faq-item']}>
                                <button
                                    className={`${styles['faq-question']} btn btn-link`}
                                    onClick={() =>
                                        setOpenAccordionItem(
                                            openAccordionItem === 'item6' ? null : 'item6',
                                        )
                                    }
                                    type="button"
                                    aria-expanded={openAccordionItem === 'item6'}
                                >
                                    <span className={styles['faq-question-text']}>
                                        Will my videos remain accessible if I downgrade or cancel my
                                        plan?
                                    </span>
                                    <span
                                        className={`${styles['faq-icon']} ${openAccordionItem === 'item6' ? styles['expanded'] : ''}`}
                                    >
                                        +
                                    </span>
                                </button>
                                <div
                                    className={`collapse ${openAccordionItem === 'item6' ? 'show' : ''}`}
                                >
                                    <div className={styles['faq-answer']}>
                                        Yes. All videos you create are yours to keep permanently.
                                        You can download and reuse them even if you cancel your
                                        plan. You will only lose access to premium features
                                        associated with higher tier subscriptions.
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="my-l5-48">
                <JumboCta
                    header="Join over 5,000,000 companies using Lumen5 to tell their stories."
                    body="Lumen5 helps marketers, publishers, and brands create video content with AI, without the hassle or technical expertise."
                />
            </div>
            <SubscriptionChangeModal
                showing={showSubscriptionChangeConfirmation}
                subscription_data={confirmationModalData}
                coupon_data={couponStatus}
                update_subscription={updateSubscription}
                close_subscription_change_modal={closeSubscriptionChangeModal}
            />
            <PlanSwitchErrorHandlerModal
                showing={showPlanSwitchErrorModal}
                errorInfo={planSwitchErrorInfo}
                onModalClose={togglePlanSwitchErrorModal}
            />
        </div>
    );
});

export default Pricing;
